import { Line2d } from '../../math/Line2d';
import { LineSeg2d } from '../../math/LineSeg2d';
import { Point2dUtils } from '../../math/Point2dUtils';
import { PathFinder } from '../../model/polygon/Region';
import { EPointOnSegmentType } from '../../types';
import { IWallInfo } from '@/editor/types/wallShape';
import { getNearestPoint, normalizeAngle } from '@/editor/math/utils';
import { intersectCommonShape, intersectRayAndLineSeg } from '@/editor/math/shape/intesection';
import { getShapeSeg, isRightAngleShape, isTangentShape } from '@/editor/math/shape/other';
import { IShapeSeg2d } from '@/editor/types/shape';
import { Ray2d } from '@/editor/math/ray2d';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { getWallDrawShape, reverseShape } from '.';
import { IShapePoint2d } from '@/editor/types/shape';
import { getTangentDirection } from '@/editor/math/shape/tangent';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { dot, getAngle, getDirectionFromAngle, opposite, rotate } from '@/editor/math/vector';
import { arcThresholdValue, thresholdValue } from '@/editor/math/const';
import { Circle2d } from '@/editor/math/circle2d';

export enum HandleType {
  Start = 'start',
  End = 'end'
}

interface IUpdateItem {
  wallId: string;
  type: HandleType;
  wallIndex: number;
}

export class CalculateHelper {
  // 存储所有关联墙体数据，用于递归处理相邻墙体端点
  protected _cacheWallMap: Map<string, IWallInfo> = new Map();

  protected _pointPointConnectedQueue: IUpdateItem[] = [];
  protected _pointWallConnectedQueue: IUpdateItem[] = [];

  protected _result: {
    wallId: string;
    type: HandleType;
    points: IShapePoint2d[];
  }[] = [];
  constructor() {}

  get result() {
    return this._result;
  }

  get cacheWallMap() {
    return this._cacheWallMap;
  }

  addCacheWall(wallId: string, wallInfo: IWallInfo) {
    this._cacheWallMap.set(wallId, wallInfo);
  }

  addToPointPointConnectedQueue(updateItem: IUpdateItem) {
    this._pointPointConnectedQueue.push(updateItem);
  }

  addToPointWallConnectedQueue(updateItem: IUpdateItem) {
    this._pointWallConnectedQueue.push(updateItem);
  }

  startChainCalculate() {
    // 纯端点连接优先处理
    this._pointPointConnectedQueue = sortAndRemoveDuplicates(this._pointPointConnectedQueue);
    while (this._pointPointConnectedQueue.length) {
      const updateItem = this._pointPointConnectedQueue.shift();
      if (!updateItem) break;

      const { wallId, type } = updateItem;

      switch (type) {
        case HandleType.Start:
          this.calExtremeOutline(wallId, type);
          break;
        case HandleType.End:
          this.calExtremeOutline(wallId, type);
          break;
      }
    }

    // 再处理包含墙体连接情况的处理
    this._pointWallConnectedQueue = sortAndRemoveDuplicates(this._pointWallConnectedQueue);
    while (this._pointWallConnectedQueue.length) {
      const updateItem = this._pointWallConnectedQueue.shift();
      if (!updateItem) break;

      const { wallId, type } = updateItem;

      switch (type) {
        case HandleType.Start:
          this.calExtremeOutline(wallId, type);
          break;
        case HandleType.End:
          this.calExtremeOutline(wallId, type);
          break;
      }
    }
  }

  protected calExtremeOutline(wallId: string, type: HandleType) {
    const mainWallInfo = this._cacheWallMap.get(wallId);

    if (!mainWallInfo || mainWallInfo.thickness === 0 || !mainWallInfo.relativeWallInfos) return;

    const mainPointOnWall =
      (type === HandleType.Start
        ? mainWallInfo.relativeWallInfos.mainStartOnWallInternal
        : mainWallInfo.relativeWallInfos.mainEndOnWallInternal) || [];
    const mainPointPointConnected =
      (type === HandleType.Start
        ? mainWallInfo.relativeWallInfos.mainStartConnectToPoint
        : mainWallInfo.relativeWallInfos.mainEndConnectToPoint) || [];

    let extremeShapePath: IShapePoint2d[] | null;

    if (mainPointOnWall.length === 0 && mainPointPointConnected.length === 0) {
      // 自由端点
      extremeShapePath = this.calFreeWallShape(mainWallInfo, type);
    } else if (mainPointOnWall.length === 0) {
      // 处理只有端点端点相连的情况
      extremeShapePath = this.calPointPointConnected(mainWallInfo, type);
    } else if (mainPointPointConnected.length === 0) {
      // 处理只有端点与墙体相连的情况
      extremeShapePath = this.calPointWallConnected(mainWallInfo, type);
    } else {
      // 处理主墙端点与墙体和其他端点都有连接的情况
      extremeShapePath = this.calMixConnected(mainWallInfo, type);
    }

    if (!extremeShapePath || extremeShapePath.length === 0) return;

    this.setLocalWallShape(wallId, type, extremeShapePath);
    this._result.push({ wallId, type, points: extremeShapePath });
  }

  private getShapeSegInfos(wallInfos: IWallInfo[]) {
    const handleWallShapeSegs: { seg: IShapeSeg2d; wallId: string }[] = [];
    wallInfos.forEach((wallInfo) => {
      const drawShape = wallInfo.drawShape;
      if (drawShape.length === 2) {
        if (wallInfo.skipCalculate) return;
        const [start, end] = drawShape;
        const shapeSeg = getShapeSeg({ start, end, center: start.center, clockwise: start.clockwise });
        handleWallShapeSegs.push({ seg: shapeSeg, wallId: wallInfo.id });
        return;
      }

      // 弧形墙体处理
      if (wallInfo.seg instanceof ArcSeg2d) {
        const startShape = wallInfo.startShape;
        const endShape = wallInfo.endShape;
        for (let index = startShape.length - 1; index > 0; index--) {
          handleWallShapeSegs.push({
            seg: new LineSeg2d(startShape[index], startShape[index - 1]),
            wallId: wallInfo.id
          });
        }

        // 左弧
        const leftSeg = wallInfo.leftSeg as ArcSeg2d;
        const rightSeg0 = wallInfo.rightSeg as ArcSeg2d;
        // startShape/endShape 约定为 [左, 右]，但未重算过端盖的弧墙保留的是 getBaseShape 的原始顺序
        // (恒把朝向圆心的点放 index 0)，顺时针弧墙由此左右颠倒；而一条墙可能一端已重算、另一端还没算，
        // 两端顺序因此互相矛盾。此时若按下标取点，左弧会取到「一个内圈点 + 一个外圈点」，
        // 而 ArcSeg2d 的半径只由 start 决定 —— 左右弧被造成同一半径，外偏移弧整条从轮廓里消失，
        // 相邻墙拿它做端盖切割时所有交点都落到错误一侧被判据剔除，端盖算不出来。
        // 这里改为按「到左/右偏移半径的距离」判定每个端盖的真实左右端。
        const pickSide = (shape: IShapePoint2d[]) => {
          const head = shape[0];
          const tail = shape[shape.length - 1];
          const err = (p: IShapePoint2d, r: number) => Math.abs(Point2dUtils.distanceTo(p, leftSeg.center) - r);
          const asIs = err(head, leftSeg.radius) + err(tail, rightSeg0.radius);
          const swapped = err(tail, leftSeg.radius) + err(head, rightSeg0.radius);
          return swapped < asIs ? { left: tail, right: head } : { left: head, right: tail };
        };
        const startSide = pickSide(startShape);
        const endSide = pickSide(endShape);

        handleWallShapeSegs.push({
          seg: new ArcSeg2d({
            start: startSide.left,
            end: endSide.left,
            center: leftSeg.center,
            clockwise: leftSeg.clockwise
          }),
          wallId: wallInfo.id
        });

        for (let index = 0; index < endShape.length - 1; index++) {
          handleWallShapeSegs.push({
            seg: new LineSeg2d(endShape[index], endShape[index + 1]),
            wallId: wallInfo.id
          });
        }

        // 右弧
        const rightSeg = wallInfo.rightSeg as ArcSeg2d;
        handleWallShapeSegs.push({
          seg: new ArcSeg2d({
            start: endSide.right,
            end: startSide.right,
            center: rightSeg.center,
            clockwise: !rightSeg.clockwise
          }),
          wallId: wallInfo.id
        });

        return;
      }

      for (let index = 0; index < drawShape.length; index++) {
        handleWallShapeSegs.push({
          seg: new LineSeg2d(drawShape[index], drawShape[(index + 1) % drawShape.length]),
          wallId: wallInfo.id
        });
      }
    });

    return handleWallShapeSegs;
  }

  // 自由墙体端点轮廓
  private calFreeWallShape(mainWallInfo: IWallInfo, type: HandleType): IPoint2d[] {
    return type === HandleType.Start
      ? [mainWallInfo.leftSeg.start, mainWallInfo.rightSeg.start]
      : [mainWallInfo.leftSeg.end, mainWallInfo.rightSeg.end];
  }

  // 获取与主墙体端点端点连接的墙体信息
  private getPointPointConnectedWallInfos(mainWallInfo: IWallInfo, type: HandleType) {
    let infos, order;
    if (type === HandleType.Start) {
      const mainStartConnectToPoint = mainWallInfo.relativeWallInfos?.mainStartConnectToPoint || [];
      // 取不到缓存墙的跳过。展开 undefined 不会报错，只会得到缺字段的假 IWallInfo，问题会漂到下游才暴露
      infos = mainStartConnectToPoint.flatMap((item) => {
        const wallInfo = this._cacheWallMap.get(item.id);
        if (!wallInfo) return [];

        return [
          {
            ...wallInfo,
            handleType: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
            id: item.id
          }
        ];
      });
      order = mainWallInfo.relativeWallInfos?.mainStartConnectToPointOrder || 0;
    } else {
      const mainEndConnectToPoint = mainWallInfo.relativeWallInfos?.mainEndConnectToPoint || [];
      infos = mainEndConnectToPoint.flatMap((item) => {
        const wallInfo = this._cacheWallMap.get(item.id);
        if (!wallInfo) return [];

        return [
          {
            ...wallInfo,
            handleType: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
            id: item.id
          }
        ];
      });
      order = mainWallInfo.relativeWallInfos?.mainEndConnectToPointOrder || 0;
    }

    return { infos, order };
  }

  // 端点端点相连
  protected calPointPointConnected(mainWallInfo: IWallInfo, type: HandleType): IShapePoint2d[] | null {
    const { infos: pointPointConnectedInfos, order } = this.getPointPointConnectedWallInfos(mainWallInfo, type);

    // ------------------没有端点端点连接的墙体------------------
    if (pointPointConnectedInfos.length === 0) return [];

    let mainLeftPoint;
    let mainRightPoint;

    const prevWallInfos = pointPointConnectedInfos.slice(0, order); // 在该墙体之前添加的墙体
    const laterWallInfos = pointPointConnectedInfos.slice(order); // 在该墙体之后添加的墙体

    if (order === 0) {
      // --- 前两条连接墙体需要特殊处理 ---
      // order 为 0 时 laterWallInfos 就是全部连接墙，上面已排除空数组，这里必能取到
      const firstLaterWallInfo = laterWallInfos.shift();
      if (firstLaterWallInfo) prevWallInfos.push(firstLaterWallInfo);
    }

    // --- 两墙端点相连 ---
    if (prevWallInfos.length === 1) {
      const handleWallInfo = prevWallInfos[0];

      const isRightAngle = isRightAngleShape(mainWallInfo.shape, handleWallInfo.shape);
      const isParallel = isTangentShape(mainWallInfo.shape, handleWallInfo.shape);
      if (isRightAngle) {
        let _mainId = mainWallInfo.id;
        let _connectedId = handleWallInfo.id;
        if (order === 1) {
          // 如果当前线段为第二条，主副线段需要交换
          [_mainId, _connectedId] = [_connectedId, _mainId];
        }
        const result = this.calLeftRightIntersectionWithRightAngle(_mainId, _connectedId);
        if (result) {
          mainLeftPoint = order === 0 ? result.mainLeft : result.otherLeft;
          mainRightPoint = order === 0 ? result.mainRight : result.otherRight;
        }
      } else if (isParallel) {
        // 平行线，需要计算垂线交点
        const result = this.calLeftRightIntersectionWithParallel(mainWallInfo.id, handleWallInfo.id, type);
        if (result) {
          mainLeftPoint = result.mainLeftPoint;
          mainRightPoint = result.mainRightPoint;
        }
      } else {
        const mainConnectedPoint = type === HandleType.Start ? mainWallInfo.seg.start : mainWallInfo.seg.end; // todo: 主要用于判断最近交点，待优化
        // 计算交点
        const result = this.calLeftRightIntersection(mainWallInfo, handleWallInfo, mainConnectedPoint, type);

        mainLeftPoint = result.mainLeft;
        mainRightPoint = result.mainRight;
      }

      return mainLeftPoint && mainRightPoint ? [mainLeftPoint, mainRightPoint] : null;
    }

    // --- 多条墙体端点相连 ---
    return this.calPointWallIntersection(prevWallInfos, mainWallInfo, type);
  }

  // 端点与墙体相连
  protected calPointWallConnected(mainWallInfo: IWallInfo, type: HandleType): IShapePoint2d[] | null {
    const handleWallInfos = (
      (type === HandleType.Start
        ? mainWallInfo.relativeWallInfos?.mainStartOnWallInternal
        : mainWallInfo.relativeWallInfos?.mainEndOnWallInternal) || []
    )
      .map((item) => this._cacheWallMap.get(item.id))
      .filter((item) => item !== undefined);

    // 端点落在墙体内部(T 型)：两侧拼到相邻墙朝向主墙本体那条边，避免嵌入墙体
    return this.calPointWallIntersection(handleWallInfos, mainWallInfo, type, true);
  }

  protected calMixConnected(mainWallInfo: IWallInfo, type: HandleType): IShapePoint2d[] | null {
    const mainPointPointConnected =
      (type === HandleType.Start
        ? mainWallInfo.relativeWallInfos?.mainStartConnectToPoint
        : mainWallInfo.relativeWallInfos?.mainEndConnectToPoint) || [];
    const mainPointPointConnectedOrder =
      type === HandleType.Start
        ? mainWallInfo.relativeWallInfos?.mainStartConnectToPointOrder
        : mainWallInfo.relativeWallInfos?.mainEndConnectToPointOrder || 0;

    const mainPointWallConnected =
      (type === HandleType.Start
        ? mainWallInfo.relativeWallInfos?.mainStartOnWallInternal
        : mainWallInfo.relativeWallInfos?.mainEndOnWallInternal) || [];

    const handleWallInfos = [
      ...mainPointPointConnected.slice(0, mainPointPointConnectedOrder),
      ...mainPointWallConnected
    ]
      .map((item) => this._cacheWallMap.get(item.id))
      .filter((item) => item !== undefined);

    return this.calPointWallIntersection(handleWallInfos, mainWallInfo, type);
  }

  protected calLeftRightIntersection(
    mainWallInfo: IWallInfo,
    otherWallInfo: IWallInfo,
    mainConnectedPoint: IShapePoint2d,
    type: HandleType
  ): { mainLeft: IShapePoint2d | null; mainRight: IShapePoint2d | null } {
    const mainLeftLine = mainWallInfo.leftShape;
    const mainRightLine = mainWallInfo.rightShape;
    const mainLeftSeg = mainWallInfo.leftSeg;
    const mainRightSeg = mainWallInfo.rightSeg;

    const isStartEndConnected =
      Point2dUtils.equals(otherWallInfo.seg.start, mainWallInfo.seg.end) ||
      Point2dUtils.equals(otherWallInfo.seg.end, mainWallInfo.seg.start);
    let otherLeftLine = otherWallInfo.leftShape;
    let otherRightLine = otherWallInfo.rightShape;
    if (!isStartEndConnected) [otherLeftLine, otherRightLine] = [otherRightLine, otherLeftLine];

    let isArcArcIntersect = false;
    if (mainWallInfo.seg instanceof ArcSeg2d && otherWallInfo.seg instanceof ArcSeg2d) {
      isArcArcIntersect = true;
    }

    const mainLeftPoints = intersectCommonShape(mainLeftLine, otherLeftLine);
    const mainRightPoints = intersectCommonShape(mainRightLine, otherRightLine);
    if (mainLeftPoints && mainRightPoints) {
      const mainLeft = getNearestPoint(mainLeftPoints, mainConnectedPoint);
      const mainRight = getNearestPoint(mainRightPoints, mainConnectedPoint);

      // 弧-弧接头交点合理性校验：两弧近相切/近同心时(如厚度差大的双弧透镜)，
      // 用两整圆求交得到的最近交点仍可能离共享端点数千毫米(接头处偏移边近乎平行，真实 miter 点不在整圆的全局交点里)。
      // 正常弧-弧接头的真实 miter 点离连接点仅约"半个~几个壁厚"量级；这里若明显超出该量级，判定整圆求交无效，
      // 落到下方 fallback(弧墙自身偏移端点，本就簇在共享端点附近)，避免端头飞走导致轮廓自交/闭合失败。
      if (isArcArcIntersect && mainLeft && mainRight) {
        const maxThickness = Math.max(mainWallInfo.thickness, otherWallInfo.thickness);
        const miterLimit = maxThickness * 10;
        const leftFar = Point2dUtils.distanceTo(mainLeft, mainConnectedPoint) > miterLimit;
        const rightFar = Point2dUtils.distanceTo(mainRight, mainConnectedPoint) > miterLimit;
        if (leftFar || rightFar) {
          return {
            mainLeft: type === HandleType.Start ? mainLeftSeg.start : mainLeftSeg.end,
            mainRight: type === HandleType.Start ? mainRightSeg.start : mainRightSeg.end
          };
        }
      }

      return { mainLeft, mainRight };
    }

    return {
      mainLeft: type === HandleType.Start ? mainLeftSeg.start : mainLeftSeg.end,

      mainRight: type === HandleType.Start ? mainRightSeg.start : mainRightSeg.end
    };
  }

  protected calLeftRightIntersectionWithRightAngle(
    mainId: string,
    otherId: string
  ): null | {
    mainLeft: IShapePoint2d | null;
    mainRight: IShapePoint2d | null;
    otherLeft: IShapePoint2d | null;
    otherRight: IShapePoint2d | null;
  } {
    const mainWallInfo = this._cacheWallMap.get(mainId);
    const otherWallInfo = this._cacheWallMap.get(otherId);
    if (!mainWallInfo || !otherWallInfo) return null;

    const mainSeg = mainWallInfo.seg;
    const otherSeg = otherWallInfo.seg;
    const mainStart = mainSeg.start;
    const mainEnd = mainSeg.end;
    const otherStart = otherSeg.start;
    const otherEnd = otherSeg.end;
    const mainLeftLine = mainWallInfo.leftShape;
    const mainRightLine = mainWallInfo.rightShape;
    const otherLeftLine = otherWallInfo.leftShape;
    const otherRightLine = otherWallInfo.rightShape;

    const mainLeftLeftPoints = intersectCommonShape(mainLeftLine, otherLeftLine);
    const mainRightRightPoints = intersectCommonShape(mainRightLine, otherRightLine);
    const mainLeftRightPoints = intersectCommonShape(mainLeftLine, otherRightLine);
    const mainRightLeftPoints = intersectCommonShape(mainRightLine, otherLeftLine);
    const mainLeftLeft = mainLeftLeftPoints ? getNearestPoint(mainLeftLeftPoints, mainStart) : null;
    const mainRightRight = mainRightRightPoints ? getNearestPoint(mainRightRightPoints, mainEnd) : null;
    const mainLeftRight = mainLeftRightPoints ? getNearestPoint(mainLeftRightPoints, mainEnd) : null;
    const mainRightLeft = mainRightLeftPoints ? getNearestPoint(mainRightLeftPoints, mainStart) : null;

    let isOtherOnMainLeft = false;
    {
      const otherMid = Point2dUtils.getCenter(otherStart, otherEnd);
      const startSide = mainSeg.pointSide(otherMid);
      if (startSide > 0) {
        isOtherOnMainLeft = true;
      } else if (startSide < 0) {
        isOtherOnMainLeft = false;
      } else {
        const endSide = mainSeg.pointSide(otherStart);
        if (endSide > 0) {
          isOtherOnMainLeft = true;
        } else if (endSide < 0) {
          isOtherOnMainLeft = false;
        }
      }
    }

    // 8种情况列举
    if (Point2dUtils.equals(mainStart, otherStart)) {
      if (isOtherOnMainLeft) {
        return {
          mainLeft: mainLeftLeft,
          mainRight: mainRightLeft,
          otherLeft: mainLeftLeft,
          otherRight: mainLeftRight
        };
      } else {
        return {
          mainLeft: mainLeftRight,
          mainRight: mainRightRight,
          otherLeft: mainRightLeft,
          otherRight: mainRightRight
        };
      }
    } else if (Point2dUtils.equals(mainStart, otherEnd)) {
      if (isOtherOnMainLeft) {
        return {
          mainLeft: mainLeftRight,
          mainRight: mainRightRight,
          otherLeft: mainLeftLeft,
          otherRight: mainLeftRight
        };
      } else {
        return {
          mainLeft: mainLeftLeft,
          mainRight: mainRightLeft,
          otherLeft: mainRightLeft,
          otherRight: mainRightRight
        };
      }
    } else if (Point2dUtils.equals(mainEnd, otherStart)) {
      if (isOtherOnMainLeft) {
        return {
          mainLeft: mainLeftRight,
          mainRight: mainRightRight,
          otherLeft: mainLeftLeft,
          otherRight: mainLeftRight
        };
      } else {
        return {
          mainLeft: mainLeftLeft,
          mainRight: mainRightLeft,
          otherLeft: mainRightLeft,
          otherRight: mainRightRight
        };
      }
    } else if (Point2dUtils.equals(mainEnd, otherEnd)) {
      if (isOtherOnMainLeft) {
        return {
          mainLeft: mainLeftLeft,
          mainRight: mainRightLeft,
          otherLeft: mainLeftLeft,
          otherRight: mainLeftRight
        };
      } else {
        return {
          mainLeft: mainLeftRight,
          mainRight: mainRightRight,
          otherLeft: mainRightLeft,
          otherRight: mainRightRight
        };
      }
    }

    return null;
  }

  protected calLeftRightIntersectionWithParallel(
    mainId: string,
    otherId: string,
    type: HandleType
  ): null | { mainLeftPoint: IShapePoint2d | null; mainRightPoint: IShapePoint2d | null } {
    const mainWallInfo = this._cacheWallMap.get(mainId);
    const otherWallInfo = this._cacheWallMap.get(otherId);
    if (!mainWallInfo || !otherWallInfo) return null;

    const mainSeg = mainWallInfo.seg;
    const otherSeg = otherWallInfo.seg;
    const mainConnectedPoint = type === HandleType.Start ? mainWallInfo.seg.start : mainWallInfo.seg.end;
    const otherConnectedPoint = Point2dUtils.equals(otherSeg.start, mainConnectedPoint) ? otherSeg.start : otherSeg.end;
    const midPoint = {
      x: (mainConnectedPoint.x + otherConnectedPoint.x) / 2,
      y: (mainConnectedPoint.y + otherConnectedPoint.y) / 2
    };
    const perpendicularSeg = mainSeg.getPerpendicularLine(midPoint);
    const perpendicularLine = new Line2d({
      points: { p1: perpendicularSeg.start, p2: perpendicularSeg.end }
    });
    const mainLeftLine = mainWallInfo.leftShape;
    const mainRightLine = mainWallInfo.rightShape;

    const leftPoints = intersectCommonShape(perpendicularLine, mainLeftLine);
    const rightPoints = intersectCommonShape(perpendicularLine, mainRightLine);

    return {
      mainLeftPoint: leftPoints ? getNearestPoint(leftPoints, mainConnectedPoint) : null,
      mainRightPoint: rightPoints ? getNearestPoint(rightPoints, mainConnectedPoint) : null
    };
  }

  protected setLocalWallShape(wallId: string, type: HandleType, points: IShapePoint2d[]) {
    const mainWallInfo = this._cacheWallMap.get(wallId);
    if (!mainWallInfo) return;

    if (type === HandleType.Start) {
      mainWallInfo.startShape = [...points];
    } else {
      mainWallInfo.endShape = [...points];
    }

    mainWallInfo.drawShape = getWallDrawShape(
      {
        start: mainWallInfo.start,
        end: mainWallInfo.end,
        center: mainWallInfo.center,
        clockwise: mainWallInfo.clockwise
      },
      mainWallInfo.startShape,
      mainWallInfo.endShape
    );
  }

  // 计算主墙体端点与多条墙体相连的情况
  // 点是否落在弧墙自身的厚度带内(到圆心距离处于左右偏移半径之间)。
  // 用于校验单侧兜底插入的端盖中间点：越出带外说明它不是本墙封口上的点，插进去会让轮廓自交。
  private isInsideArcWallBand(point: IPoint2d, leftSeg: IShapeSeg2d, rightSeg: IShapeSeg2d): boolean {
    if (!(leftSeg instanceof ArcSeg2d) || !(rightSeg instanceof ArcSeg2d)) return true;
    const radius = Point2dUtils.distanceTo(point, leftSeg.center);
    const min = Math.min(leftSeg.radius, rightSeg.radius);
    const max = Math.max(leftSeg.radius, rightSeg.radius);
    return radius >= min - arcThresholdValue && radius <= max + arcThresholdValue;
  }

  protected calPointWallIntersection(
    subWallInfos: IWallInfo[],
    mainWallInfo: IWallInfo,
    type: HandleType,
    pointOnWall: boolean = false
  ): IShapePoint2d[] | null {
    const subWallShapeSegInfos = this.getShapeSegInfos(subWallInfos);
    const subWallShapeSegs = subWallShapeSegInfos.map(({ seg }) => seg);
    const mainLeftSeg = mainWallInfo.leftSeg;
    const mainRightSeg = mainWallInfo.rightSeg;

    // wallId → 相邻墙中线(Circle2d/Line2d)；主墙本体参考点 = 主墙中线中点。
    // 用于判断交点落在相邻墙的哪一侧，从而两侧都拼到主墙本体所在那条边。
    const subCenterMap = new Map<string, Circle2d | Line2d>();
    subWallInfos.forEach((w) => {
      if (w.shape instanceof Circle2d || w.shape instanceof Line2d) subCenterMap.set(w.id, w.shape);
    });
    const mainSegC = mainWallInfo.seg;
    const bodyRef =
      mainSegC instanceof ArcSeg2d
        ? mainSegC.getMidPoint()
        : { x: (mainSegC.start.x + mainSegC.end.x) / 2, y: (mainSegC.start.y + mainSegC.end.y) / 2 };

    // 计算交点
    let minLeftIndex = -1;
    let minRightIndex = -1;
    let mainLeftPoint;
    let mainRightPoint;
    let extremeShapePath: IShapePoint2d[] | null = null;

    const maxThickness = Math.max(mainWallInfo.thickness, ...subWallInfos.map((w) => w.thickness || 0));

    // 延长线相交检测
    const leftIntersect = extendedSegIntersectSegs({
      mainSeg: mainLeftSeg,
      subSegInfos: subWallShapeSegInfos,
      handleType: type,
      isLeft: true,
      subCenterMap,
      bodyRef,
      pointOnWall,
      maxThickness
    });
    const rightIntersect = extendedSegIntersectSegs({
      mainSeg: mainRightSeg,
      subSegInfos: subWallShapeSegInfos,
      handleType: type,
      isLeft: false,
      subCenterMap,
      bodyRef,
      pointOnWall,
      maxThickness
    });

    if (leftIntersect) {
      mainLeftPoint = leftIntersect.point;
      minLeftIndex = leftIntersect.index;
    }
    if (rightIntersect) {
      mainRightPoint = rightIntersect.point;
      minRightIndex = rightIntersect.index;
    }

    // 两边都有交点，且交点在同一条线段上，则直接返回
    // todo 这里如果mainWallInfo是弧形墙时有种特殊情况就是minLeftIndex !== minRightIndex时边界线会交叉暂时这样处理
    // if (mainLeftPoint && mainRightPoint && minLeftIndex === minRightIndex) {
    // 弧形主墙 T 型接头单侧偏移弧够不到目标侧墙的纠正：
    // 当弧墙端点落在同一面目标侧墙内(subWallInfos 为该单面墙)、左右偏移弧交点却落在该墙轮廓的不同边
    // (minLeftIndex !== minRightIndex)时，说明其中一侧偏移弧(通常是内壁偏移弧，半径过大或弧扫角超半圆)
    // 根本够不到目标侧墙内壁，用整圆延长求交时绕回到了该侧墙轮廓的错误边(如封口边)，得到一个悬空错配点。
    // T 型里弧端点恰落在目标侧墙中线上，正确那侧的交点必落在"离弧连接点最近"的边(≈半个侧墙厚)，错配侧则远得多。
    // 这里把错配侧的点丢弃，交由下方单侧兜底用切线法向 Ray 把它重投影到目标侧墙内壁并纳入切割点，形成贴墙多点封口。
    // 只在"同一面墙的不同边 + 一侧明显更远"时介入；相邻是多面墙(交点本就该落在不同墙)或两侧都贴墙的正常情形不动。
    const singleSubWallId =
      minLeftIndex >= 0 &&
      minRightIndex >= 0 &&
      subWallShapeSegInfos[minLeftIndex]?.wallId === subWallShapeSegInfos[minRightIndex]?.wallId;
    if (mainWallInfo.center && mainLeftPoint && mainRightPoint && minLeftIndex !== minRightIndex && singleSubWallId) {
      const connPoint = type === HandleType.Start ? mainWallInfo.seg.start : mainWallInfo.seg.end;
      // 悬空错配的判据必须看"交点本身离连接点多远"，而非"被归属到的那条边离连接点多远"。
      // 相邻墙的封口边可能与本段连接点所在基线共线(如两段相切弧共处一条基线，前一段封口边落在该基线上)，
      // 此时正确的就近偏移交点会被整圆延长求交归属到那条远处的共线封口边上；若按"边距离"判定，
      // 就会把这个其实贴在连接点旁的正确交点误判为悬空而丢弃，兜底 Ray 再补出一个真正悬空的错点。
      // 改用"交点到连接点的距离"：两侧都贴着连接点(正常 butt 封口)时保留 2 点，只有一侧交点确实飞远才纠正。
      const leftDist = Point2dUtils.distanceTo(mainLeftPoint, connPoint);
      const rightDist = Point2dUtils.distanceTo(mainRightPoint, connPoint);
      const nearDist = Math.min(leftDist, rightDist);
      const farDist = Math.max(leftDist, rightDist);
      // 一侧明显更远(悬空错配)才纠正；两侧都贴墙(距离相当)属正常，保持返回 2 点
      if (farDist > nearDist + mainWallInfo.thickness) {
        if (leftDist <= rightDist) {
          mainRightPoint = undefined;
          minRightIndex = -1;
        } else {
          mainLeftPoint = undefined;
          minLeftIndex = -1;
        }
      } else {
        return [mainLeftPoint, mainRightPoint];
      }
    } else if (mainLeftPoint && mainRightPoint && (minLeftIndex === minRightIndex || mainWallInfo.center)) {
      return [mainLeftPoint, mainRightPoint];
    }

    // 一侧有交点的情况，取端点边线再算一次
    let extraSeg: LineSeg2d | null = null; // 直角点到其他轮廓交点的线段
    // 弧形主墙单侧兜底时：缺失侧偏移弧端点被拉直到相邻墙面产生的"切割交点"(落在相邻墙被弧墙切断的那条面上)。
    // 它必须作为端头 shape 的中间点插入，端头才会贴着相邻墙面走，而不是从墙面斜拉一条直线到弧偏移端点。
    let arcCutPoint: IPoint2d | undefined;
    if (mainLeftPoint && !mainRightPoint) {
      let direction = getTangentDirection(
        mainRightSeg,
        type === HandleType.Start ? mainRightSeg.start : mainRightSeg.end
      );
      direction = Point2dUtils.rotateDirection(direction, Math.PI / 2);
      const mainRay = new Ray2d(type === HandleType.Start ? mainRightSeg.start : mainRightSeg.end, direction);
      const intersect = rayIntersectSegments(mainRay, subWallShapeSegs);
      if (intersect) {
        mainRightPoint = mainRay.origin;
        minRightIndex = intersect.index;
        if (!Point2dUtils.equals(mainRightPoint, intersect.point)) {
          extraSeg = new LineSeg2d(intersect.point, mainRightPoint);
          arcCutPoint = intersect.point;
        }
      }
    } else if (mainRightPoint && !mainLeftPoint) {
      let direction = getTangentDirection(mainLeftSeg, type === HandleType.Start ? mainLeftSeg.start : mainLeftSeg.end);
      direction = Point2dUtils.rotateDirection(direction, -Math.PI / 2);
      const mainRay = new Ray2d(type === HandleType.Start ? mainLeftSeg.start : mainLeftSeg.end, direction);
      const intersect = rayIntersectSegments(mainRay, subWallShapeSegs);
      if (intersect) {
        mainLeftPoint = mainRay.origin;
        minLeftIndex = intersect.index;
        if (!Point2dUtils.equals(mainLeftPoint, intersect.point)) {
          extraSeg = new LineSeg2d(mainLeftPoint, intersect.point);
          arcCutPoint = intersect.point;
        }
      }
    }

    if (mainWallInfo.center) {
      // 弧形主墙单侧兜底：一侧偏移弧够不到相邻墙(如厚弧墙嵌薄侧墙，内壁偏移弧半径过大)时，
      // 上面已参照直墙分支用切线法向 Ray 为缺失侧补出了端头点(arcCutPoint = 相邻墙被切割处的交点)。
      if (!mainLeftPoint || !mainRightPoint) {
        return null;
      }
      // 有切割交点：端头为 3 点 [mainLeftPoint, arcCutPoint, mainRightPoint]，把切割点插到中间，
      // 让封口先落到相邻墙的切割面、再收到弧墙偏移端点，贴合墙面且不自交(顺序遵循 left→right 约定)。
      // 但这个中间点必须真的落在本墙自己的厚度带内：射线打在相邻墙轮廓上得到的交点，可能略微越出本墙的
      // 偏移弧(实测超出 0.12mm、且离另一侧端点仅 1.75mm)。此时端盖会先探出自身右偏移弧再收回来，
      // 与该偏移弧交叉 —— 墙体轮廓自相交，PathFinder 在此处打断错误，整块区域随之丢失。
      // 上面的去重守卫只比了射线原点那一侧(本例相距 100mm)，拦不住贴着另一侧的情况，这里按半径补判：
      // 越出带外的中间点对封口没有贡献，丢弃、退回两点平口。
      if (arcCutPoint && this.isInsideArcWallBand(arcCutPoint, mainLeftSeg, mainRightSeg)) {
        return [mainLeftPoint, arcCutPoint, mainRightPoint];
      }
      // 无切割交点(两点已落在同一条边上)：直接返回 left→right 两点。
      return [mainLeftPoint, mainRightPoint];
    }
    // 计算中间交点
    if (mainLeftPoint && mainRightPoint && minLeftIndex !== minRightIndex) {
      const segments = extraSeg ? [extraSeg, ...subWallShapeSegs] : subWallShapeSegs;
      const finder = new PathFinder({ segments });
      const [startPoint, endPoint] =
        type === HandleType.Start ? [mainLeftPoint, mainRightPoint] : [mainRightPoint, mainLeftPoint];
      extremeShapePath = finder.getPath(startPoint, endPoint);

      // 终点 需要进行反转(端点轮廓路径遵循left到right)
      if (type === HandleType.End) extremeShapePath = reverseShape(extremeShapePath);
    }

    if (!extremeShapePath) return null;

    {
      // 路径重复点处理
      const _points = [];
      for (let index = 0; index < extremeShapePath.length; index++) {
        if (
          index < extremeShapePath.length - 1 &&
          Point2dUtils.equals(extremeShapePath[index], extremeShapePath[index + 1])
        ) {
          index++;
          continue;
        }

        _points.push(extremeShapePath[index]);
      }
      extremeShapePath = _points;
    }

    if (extremeShapePath.length < 2) return null;

    // 端盖是局部特征：沿相邻墙轮廓找出的路径只允许在自身两端点(左右偏移交点)附近绕过相邻墙的墙厚。
    // 主墙端点既落在某条墙身上、又与另一条墙端点相连时(calMixConnected)，左右偏移可能落在互不相邻的
    // 两条边上 —— 本例主墙左偏移落在弧墙内偏移弧、右偏移落在相邻直墙【本接头处的端盖面】上，
    // 轮廓图里这两条边之间没有短路可走，PathFinder 只能绕相邻墙整整一圈，端盖被甩到 7m 外
    // 相邻墙的另一端(还会横穿主墙自身偏移边、轮廓自相交)。这类路径一律丢弃，
    // 退回两点平口(与上方「两侧落在同一条边」时相同的处理)。
    // 走到这里 extremeShapePath 必然来自「两侧端点齐备」那个分支（唯一的赋值处），故两点都在；
    // 真缺了就没有平口可退，直接把找到的路径原样返回
    if (!mainLeftPoint || !mainRightPoint) return extremeShapePath;

    const connectPoint = type === HandleType.Start ? mainWallInfo.seg.start : mainWallInfo.seg.end;
    const reach =
      Math.max(
        Point2dUtils.distanceTo(mainLeftPoint, connectPoint),
        Point2dUtils.distanceTo(mainRightPoint, connectPoint)
      ) +
      3 * maxThickness;
    if (extremeShapePath.some((p) => Point2dUtils.distanceTo(p, connectPoint) > reach)) {
      return [mainLeftPoint, mainRightPoint];
    }

    return extremeShapePath;
  }

  clear() {
    this._cacheWallMap.clear();
    this._pointPointConnectedQueue = [];
    this._pointWallConnectedQueue = [];
    this._result = [];
  }
}

function rayIntersectSegments(ray2d: Ray2d, segments: IShapeSeg2d[]) {
  let minDistance = Infinity;
  let point;
  let index = -1;
  segments.forEach((seg, _index) => {
    // const intersect = intersectRayAndLineSeg(ray2d, seg);

    const intersect = intersectCommonShape(ray2d, seg);

    if (!intersect) return;

    const p = intersect[0]; // TODO: 待优化

    const distance = Point2dUtils.distanceTo(ray2d.origin, p);
    if (distance < minDistance) {
      minDistance = distance;
      point = p;
      index = _index;
    }
  });

  return point ? { point, index, distance: minDistance } : null;
}

interface IExtendedSegIntersectSegsParams<T = IShapeSeg2d, K = IShapeSeg2d> {
  mainSeg: T;
  subSegInfos: { seg: K; wallId: string }[];
  handleType: HandleType;
  isLeft?: boolean; // mainSeg 是主墙左偏移边还是右偏移边（弧形主墙选交点的"穿进/穿出"侧需据此反转）
  // wallId → 相邻墙中线(Circle2d/Line2d)，用于判断交点落在相邻墙的哪一侧。
  subCenterMap?: Map<string, Circle2d | Line2d>;
  // 主墙本体参考点(中线中点)：交点须与它在相邻墙中线的同一侧才保留。
  bodyRef?: IPoint2d;
  // 主墙端点是否落在相邻墙内部(T 型)。true：直墙相邻墙也按"本体同侧"拼边，避免嵌入墙体；false(端点端点相连)：直墙用 dot 斜接。
  pointOnWall?: boolean;
  // 本接头处最大墙厚：判定"掠射交点"是否还贴着接头的绝对尺度(见 extendedLineIntersectSegments)。
  maxThickness?: number;
}

// 偏移边与相邻墙边"近乎相切"的判据：夹角 < 3°(|cos| > 0.9986)。
const nearParallelDot = Math.cos((3 * Math.PI) / 180);

// 点 p 相对中线 shape 的有符号侧：圆→到圆心距离-半径(正=外/负=内)；直线→叉积(两点取自中线，符号随画线方向，但与另一点相乘后方向无关)。
function signedSideOfCenterline(p: IPoint2d, shape: Circle2d | Line2d): number {
  if (shape instanceof Circle2d) {
    return Point2dUtils.distanceTo(p, shape.center) - shape.radius;
  }
  const [a, b] = shape.getPoints();
  return (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
}

interface IExtendedSegIntersectSegsResult {
  point: IPoint2d;
  index: number;
}

function extendedSegIntersectSegs(params: IExtendedSegIntersectSegsParams): IExtendedSegIntersectSegsResult | null {
  const { mainSeg, subSegInfos } = params;

  const allSegIsStraight = subSegInfos.every(({ seg }) => seg instanceof LineSeg2d) && mainSeg instanceof LineSeg2d;

  if (allSegIsStraight) {
    return extendedLineLineIntersectSegments(params as IExtendedSegIntersectSegsParams<LineSeg2d, LineSeg2d>);
  } else if (mainSeg instanceof LineSeg2d) {
    return extendedLineIntersectSegments(params as IExtendedSegIntersectSegsParams<LineSeg2d>);
  } else if (mainSeg instanceof ArcSeg2d) {
    return extendedArcIntersectSegments(params as IExtendedSegIntersectSegsParams<ArcSeg2d>);
  }

  return null;
}

interface IIntersectPointInfo {
  point: IPoint2d;
  index: number;
  angle: number;
  wallId: string;
  intersectCount: number;
}

// 求弧形线段与线段数组相交点，返回最邻近点，startAsOrigin为true表示求起点相邻点
function extendedArcIntersectSegments(
  params: IExtendedSegIntersectSegsParams<ArcSeg2d>
): IExtendedSegIntersectSegsResult | null {
  const {
    mainSeg: mainArc,
    subSegInfos,
    handleType,
    isLeft = false,
    subCenterMap,
    bodyRef,
    pointOnWall = false
  } = params;
  const circle = mainArc.createCircle();

  let closestIntersectPointInfo: IIntersectPointInfo | undefined;
  function calIntersectInfo(tValue: number) {
    const intersects: IIntersectPointInfo[] = [];
    subSegInfos.forEach(({ seg, wallId }, _index) => {
      // 与子边的「延长形态」(直线→无限直线 Line2d，弧→整圆 Circle2d) 求交：
      // 端点斜接的角点常落在相邻墙边段端点之外约一个壁厚处，用有界边段会漏掉它（左右偏移不对称时尤其明显）；
      // 改用延长形态可稳定找到该角点，再由 searchClosestIntersectPoint 按扫角取离连接点最近的真实角点。
      const extendedSeg =
        seg instanceof LineSeg2d
          ? new Line2d({ points: { p1: seg.start, p2: seg.end } })
          : seg instanceof ArcSeg2d
            ? new Circle2d({ center: seg.center, radius: seg.radius })
            : seg;
      const _intersects = intersectCommonShape(circle, extendedSeg, tValue);
      if (!_intersects) return;
      // 圆与弧相交有可能是两个交点
      if (_intersects.length === 2) {
        if (Point2dUtils.equals(_intersects[0], _intersects[1], arcThresholdValue)) {
          _intersects.splice(1, 1);
        }
      }

      const curIntersectInfos: IIntersectPointInfo[] = [];
      _intersects.forEach((p) => {
        const segAngle = getAngle(getTangentDirection(seg, p));
        curIntersectInfos.push({
          point: p,
          index: _index,
          angle: segAngle,
          wallId,
          intersectCount: _intersects.length
        });
      });
      const curClosestIntersectPointInfo = searchClosestIntersectPoint(
        handleType,
        curIntersectInfos,
        mainArc,
        isLeft,
        subCenterMap,
        bodyRef,
        pointOnWall
      );
      if (curClosestIntersectPointInfo) {
        intersects.push(curClosestIntersectPointInfo);
      }
    });

    if (intersects.length)
      // 搜索和mainArc扫角最接近的点
      closestIntersectPointInfo = searchClosestIntersectPoint(
        handleType,
        intersects,
        mainArc,
        isLeft,
        subCenterMap,
        bodyRef,
        pointOnWall
      );
  }

  calIntersectInfo(arcThresholdValue);

  // todo
  // 计算到的交点与对于连接点的距离
  if (closestIntersectPointInfo) {
    const intersectConnectDistance = Point2dUtils.distanceTo(
      closestIntersectPointInfo.point,
      handleType === HandleType.Start ? mainArc.start : mainArc.end
    );
    if (intersectConnectDistance > mainArc.radius) {
      //可能存在一些因误差而丢失的点
      calIntersectInfo(0.1);
    }
  }

  return closestIntersectPointInfo
    ? { point: closestIntersectPointInfo.point, index: closestIntersectPointInfo.index }
    : null;
}

function searchClosestIntersectPoint(
  handleType: HandleType,
  intersectInfos: IIntersectPointInfo[],
  mainArc: ArcSeg2d,
  isLeft: boolean,
  subCenterMap?: Map<string, Circle2d | Line2d>,
  bodyRef?: IPoint2d,
  pointOnWall: boolean = false
) {
  const circle = mainArc.createCircle();
  const clockwise = mainArc.clockwise;

  let minAngleDiff = Infinity;
  let closestIntersectPointInfo: IIntersectPointInfo | undefined;

  // 端点落墙内(pointOnWall)分支：侧判定剔除错误侧后，在剩余交点里取离连接点最近的，
  // 以处理相切时偏移圆与墙边交于多点(近/远)、远点仍在本体侧且在跨度内的情形。
  const connectionPoint = handleType === HandleType.Start ? mainArc.start : mainArc.end;
  let minConnDist = Infinity;
  let minBeyond = Infinity; // 并列时用：交点超出弧本体跨度的量(0=仍在跨度内)

  function calMinSweepAngle(intersectPointInfo: IIntersectPointInfo) {
    // 计算扫角
    const intersectPointAngle = circle.getPointAngle(intersectPointInfo.point);
    let intersectSweepAngle = 0;

    if (handleType === HandleType.End && !clockwise) {
      intersectSweepAngle = intersectPointAngle - mainArc.startAngle;
    } else if (handleType === HandleType.End && clockwise) {
      intersectSweepAngle = mainArc.startAngle - intersectPointAngle;
    } else if (handleType === HandleType.Start && clockwise) {
      intersectSweepAngle = intersectPointAngle - mainArc.endAngle;
    } else if (handleType === HandleType.Start && !clockwise) {
      intersectSweepAngle = mainArc.endAngle - intersectPointAngle;
    }

    if (intersectSweepAngle === 0) return;
    intersectSweepAngle = normalizeAngle(intersectSweepAngle);
    const diffSweep = Math.abs(intersectSweepAngle - mainArc.getSweepAngle());

    if (diffSweep < minAngleDiff) {
      minAngleDiff = diffSweep;
      closestIntersectPointInfo = intersectPointInfo;
    }
  }

  intersectInfos.forEach((intersectPointInfo) => {
    // 判断弧线是穿进墙还是穿出墙，过滤掉出墙
    // 默认墙体轮廓按顺时针绘制，轮廓线左侧为外部，右侧为内部
    let arcDirectionOnIntersectPoint = getTangentDirection(mainArc, intersectPointInfo.point); // 交点在主弧线上的方向
    if (handleType === HandleType.Start) arcDirectionOnIntersectPoint = opposite(arcDirectionOnIntersectPoint);
    const segDirectionOnIntersectPoint = getDirectionFromAngle(intersectPointInfo.angle); // 线段切线方向
    const segLeftDirection = rotate(segDirectionOnIntersectPoint, Math.PI / 2);
    // intersectCount === 1 说明弧墙与该边相切，不过滤；count > 1 时过滤掉"出墙"的一侧，保留拼接侧交点。
    // 端点端点相连(!pointOnWall)：左右偏移都用同一判据 → 两侧拼到同一条边；
    // 端点落在墙体内部：主墙左偏移边与右偏移边分处轮廓两侧，要保留的"穿进"侧 dot 符号相反，左偏移用原判据、右偏移翻转 → 左右各取最近。
    const _dot = dot(arcDirectionOnIntersectPoint, segLeftDirection);
    // 主墙左右两侧偏移都拼到相邻墙同一条边。判据分情形：
    //   · 圆弧相邻墙（任意连接）/ 直墙相邻墙且端点落墙内(pointOnWall)：用「本体同侧」侧判定
    //     （圆=到圆心距离−半径；直线=叉积，二者与本体相乘后方向无关），保留与主墙本体 bodyRef 同侧的交点
    //     —— 这样弧端拼到相邻墙朝向本体那条边，不会嵌入墙体；
    //   · 直墙相邻墙且端点端点相连：沿用 dot 斜接判据(Start 保正 / End 保负)。
    const subCenter = subCenterMap?.get(intersectPointInfo.wallId);
    let useSide = !!(subCenter && bodyRef && pointOnWall);
    // 例外：主弧与相邻「圆」相切(连接点与主弧圆心、相邻圆心共线)时，本体整体落在相邻圆内/外某一侧，
    // 侧判定会把正确的就近 butt 封口点(落在另一侧)误删，只剩远端交点。这种情况改用下方 dot 取就近封口。
    if (useSide && subCenter instanceof Circle2d) {
      const v1x = connectionPoint.x - mainArc.center.x;
      const v1y = connectionPoint.y - mainArc.center.y;
      const v2x = connectionPoint.x - subCenter.center.x;
      const v2y = connectionPoint.y - subCenter.center.y;
      const m = Math.hypot(v1x, v1y) * Math.hypot(v2x, v2y) || 1;
      const sinAngle = Math.abs(v1x * v2y - v1y * v2x) / m;
      if (sinAngle < 0.02) useSide = false; // 相切
    }
    if (useSide && subCenter && bodyRef) {
      // 仅「端点落在墙内部(T 型)且非相切圆」用本体同侧侧判定；其余走下方 dot 判据取就近拼接侧。
      // 侧判定明确：与主墙本体异侧的交点一律丢弃（含相切 count===1 的点，否则相切处会误留在错误一侧 → 嵌入墙体）
      const sideP = signedSideOfCenterline(intersectPointInfo.point, subCenter);
      const sideBody = signedSideOfCenterline(bodyRef, subCenter);
      if (sideP * sideBody < 0) return;
      // 主墙与相邻墙相切时，偏移圆与墙边线交于关于连接点对称的两点(跨度内 / 跨度外)，
      // 侧判定无法区分。丢弃落在主弧角度跨度之外的远端「幻影」交点(留容差给斜接 overshoot)，
      // 只保留沿弧本体方向的那个，避免弧边绕远路穿透墙体。
      const sweepFromStart = mainArc.getPointSweepAngle(intersectPointInfo.point);
      const arcSweep = mainArc.getSweepAngle();
      if (sweepFromStart > arcSweep + 0.2 && sweepFromStart < 2 * Math.PI - 0.2) return;

      // 剩余(本体侧、跨度内)交点里取离连接点最近的。
      // 相切/掠射搭接时(如弧墙端点切向与相邻墙平行)，偏移圆与墙边线交于关于连接点镜像对称的两点，
      // 到连接点距离几乎相等，仅靠最近距离无法区分，会随整圆求交返回顺序在
      // "跨度内(正确)"与"过端点的跨度外幻影(错误，容差 0.2 拦不住)"之间抖动。
      // 距离并列时改用"超出弧跨度的量(beyond)"判据：优先取仍在弧本体跨度内的那个。
      const d = Point2dUtils.distanceTo(intersectPointInfo.point, connectionPoint);
      const beyond = sweepFromStart > arcSweep ? Math.min(sweepFromStart - arcSweep, 2 * Math.PI - sweepFromStart) : 0;
      if (
        d < minConnDist - arcThresholdValue ||
        (Math.abs(d - minConnDist) <= arcThresholdValue && beyond < minBeyond)
      ) {
        minConnDist = d;
        minBeyond = beyond;
        closestIntersectPointInfo = intersectPointInfo;
      }
      return;
    } else {
      // 端点端点相连 + 直墙：dot 斜接判据；相切(count===1)不过滤
      const isExiting = handleType === HandleType.Start ? _dot < thresholdValue : _dot > -thresholdValue;
      if (isExiting && intersectPointInfo.intersectCount > 1) return;
    }

    calMinSweepAngle(intersectPointInfo);
  });

  return closestIntersectPointInfo;
}

function extendedLineLineIntersectSegments(
  params: IExtendedSegIntersectSegsParams<LineSeg2d, LineSeg2d>
): IExtendedSegIntersectSegsResult | null {
  const { mainSeg: mainLine, subSegInfos, handleType } = params;
  const segs = subSegInfos.map(({ seg }) => seg);
  const ray = handleType === HandleType.Start ? mainLine.createEndRay() : mainLine.createStartRay();

  let minDistance = Infinity;
  let point;
  let index = -1;
  segs.forEach((seg, _index) => {
    const intersect = intersectRayAndLineSeg(ray, seg);
    if (!intersect) return;
    const distance = Point2dUtils.distanceTo(ray.origin, intersect);
    if (distance < minDistance) {
      minDistance = distance;
      point = intersect;
      index = _index;
    }
  });

  return point ? { point, index } : null;
}

function extendedLineIntersectSegments(
  params: IExtendedSegIntersectSegsParams<LineSeg2d>
): IExtendedSegIntersectSegsResult | null {
  const { mainSeg, subSegInfos, handleType, maxThickness = Infinity } = params;
  const nearStart = handleType === HandleType.Start;
  const ray = nearStart ? mainSeg.createEndRay() : mainSeg.createStartRay();
  const intersects: { point: IPoint2d; index: number; angle: number; wallId: string }[] = [];
  subSegInfos.forEach(({ seg, wallId }, _index) => {
    const _intersects = intersectCommonShape(ray, seg);
    if (!_intersects) return;
    _intersects.forEach((p) => {
      const samePointOtherWallIndex = intersects.findIndex(({ point, wallId: _wallId }) => {
        return Point2dUtils.equals(point, p) && _wallId !== wallId;
      });

      if (samePointOtherWallIndex !== -1) {
        intersects.splice(samePointOtherWallIndex, 1);
        return;
      }

      intersects.push({ point: p, index: _index, angle: getAngle(getTangentDirection(seg, p)), wallId });
    });
  });

  if (!intersects.length) return null;

  const compareDistance = Point2dUtils.distanceTo(mainSeg.start, mainSeg.end);
  let minDistance = Infinity;
  let point;
  let index = -1;
  let mainSegDirection = Point2dUtils.getUnitDirection(mainSeg.start, mainSeg.end);
  if (nearStart) mainSegDirection = opposite(mainSegDirection);

  intersects.forEach(({ point: _p, angle, index: _segIndex }) => {
    const tangentDirection = getDirectionFromAngle(angle);
    const leftDirection = Point2dUtils.rotateDirection(tangentDirection, Math.PI / 2);
    // 交点越过主墙自身偏移端点的量(正=接头外侧，斜接会外伸；负=切进本体，剪短)
    const overshoot = Point2dUtils.distanceTo(ray.origin, _p) - compareDistance;

    // 相邻墙边与主墙偏移边近乎相切(夹角 < 3°)时，主偏移边是"擦"着这条边过去的：
    //   · 「穿进/穿出」的 dot 判据只剩 1e-2 量级的噪声，谁进谁出全凭浮点误差，靠不住；
    //   · 两条近平行边的交点被微小夹角/曲率推到远处 —— 本例后建墙从接头沿更早墙反向离开(夹角 1.3°)，
    //     交点分别落在弧内壁 193mm 外与相邻墙面 4330mm 外，都不是接头角点，只是掠射幻影。
    //     封口拼到它上面就成了斜穿整条相邻墙墙厚的斜拼。
    // 因此近平行时只认还贴着接头(|overshoot| ≤ 一个墙厚)的那个交点：它就是平口位置，
    // 即使 dot 判据认为是"穿出"也保留；更远的一律丢弃(该侧退回自身偏移端点=平口)。
    const isGrazing = Math.abs(dot(tangentDirection, mainSegDirection)) > nearParallelDot;
    if (isGrazing) {
      if (Math.abs(overshoot) > maxThickness) return;
    } else if (dot(leftDirection, mainSegDirection) >= 0) {
      return;
    }

    const distance = Math.abs(overshoot);
    if (distance < minDistance) {
      minDistance = distance;
      point = _p;
      index = _segIndex;
    }
  });

  return point ? { point, index } : null;
}

function sortAndRemoveDuplicates(items: IUpdateItem[]) {
  return items
    .sort((a, b) => (a.wallIndex - b.wallIndex !== 0 ? a.wallIndex - b.wallIndex : a.type > b.type ? 1 : -1))
    .filter((item, index) => {
      const prevItem = items[index - 1];
      return index === 0 || !(item.wallIndex === prevItem.wallIndex && item.type === prevItem.type);
    });
}
